import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

const OnboardingThemeProvider = ({ children, companySettings }) => {
  useEffect(() => {
    // Apply marketplace colors from company settings
    if (companySettings?.marketplace_colors) {
      const { marketplace_colors } = companySettings;
      const root = document.documentElement;
      
      // Helper function to convert hex to RGB
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null;
      };

      // Set CSS variables for marketplace colors
      if (marketplace_colors.primary_color) {
        const primaryRgb = hexToRgb(marketplace_colors.primary_color);
        root.style.setProperty('--succession-primary', marketplace_colors.primary_color);
        if (primaryRgb) {
          root.style.setProperty('--succession-primary-10', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
          root.style.setProperty('--succession-primary-hover', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.9)`);
        }
      }

      if (marketplace_colors.secondary_color) {
        const secondaryRgb = hexToRgb(marketplace_colors.secondary_color);
        root.style.setProperty('--succession-secondary', marketplace_colors.secondary_color);
        if (secondaryRgb) {
          root.style.setProperty('--succession-secondary-10', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
        }
      }

      if (marketplace_colors.accent_color) {
        root.style.setProperty('--succession-accent', marketplace_colors.accent_color);
      }

      if (marketplace_colors.neutral_color) {
        root.style.setProperty('--succession-neutral', marketplace_colors.neutral_color);
      }

      // Set background colors - always use white for onboarding content areas
      // The company's background_color is for the main site, not the onboarding wizard
      root.style.setProperty('--succession-bg-primary', '#ffffff');
      root.style.setProperty('--succession-bg-secondary', '#f8fafc');
      
      // Set text colors
      root.style.setProperty('--succession-text-primary', '#1f2937');
      root.style.setProperty('--succession-text-secondary', '#6b7280');
      root.style.setProperty('--succession-text-muted', '#9ca3af');
      
      // Set border colors
      root.style.setProperty('--succession-border', '#e5e7eb');
      
      // Set error colors
      root.style.setProperty('--succession-error', '#dc2626');
      root.style.setProperty('--succession-error-bg', '#fef2f2');
      
      // Set success colors
      root.style.setProperty('--succession-success', '#059669');
      root.style.setProperty('--succession-success-bg', '#d1fae5');
      
      // Set hover colors
      root.style.setProperty('--succession-hover-bg', '#f3f4f6');
      
      // Set shadow colors
      root.style.setProperty('--succession-shadow', 'rgba(0, 0, 0, 0.1)');

      // Set appropriate theme class
      const savedTheme = localStorage.getItem('succession-theme') || 'light';
      root.classList.remove('succession-light', 'succession-dark');
      root.classList.add(`succession-${savedTheme}`);
      root.classList.add('marketplace-theme');
    }
  }, [companySettings]);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = (event) => {
      const newTheme = event.detail.theme;
      const root = document.documentElement;
      
      root.classList.remove('succession-light', 'succession-dark');
      root.classList.add(`succession-${newTheme}`);
      
      // Save to localStorage
      localStorage.setItem('succession-theme', newTheme);
    };

    window.addEventListener('succession-theme-change', handleThemeChange);
    
    return () => {
      window.removeEventListener('succession-theme-change', handleThemeChange);
    };
  }, []);

  return (
    <div className="succession-onboarding-wrapper">
      {children}
    </div>
  );
};

OnboardingThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  companySettings: PropTypes.object
};

export default OnboardingThemeProvider; 